import React from "react";

import {
  TextField,
  Box,
  Button,
  InputAdornment,
  Typography,
  Switch,
  Avatar,
  AvatarGroup,
  Stack,
  Alert,
  CircularProgress,
  Chip,
  alpha,
  useTheme,
  Tooltip,
  MenuItem,
  Slider,
} from "@mui/material";

import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import GroupIcon from "@mui/icons-material/Group";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TuneIcon from "@mui/icons-material/Tune";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";

import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { useGroupMutation } from "../../Slices/circleApiSlice";

import { useForm, Controller, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { CreateGroupData, createGroupSchema } from "lib/schema";
import UserListModalContent from "../shared/UserListModal";
import { RootState } from "store";
import AddNewUsers from "./AddNewUsers";
import {
  useGetStatusQuery,
  useGetProfileQuery,
} from "components/Slices/usersApiSlice";
import { ImageAvatar } from "../shared/ImageAvatar";

const COUNTRY_CURRENCY: Record<string, string> = { US: "USD", CA: "CAD" };

// Participant-count slider bounds. Floor is 2 — a rotating circle needs at least
// two people. Ceiling gives room above the typical 8–15 without being unwieldy.
const MEMBER_RANGE = { min: 2, max: 20 };

// Payout cadence presets. `key` encodes unit + count so a single <Select> can
// drive both form fields. "Daily" exists mainly for testing short circles.
type CadenceUnit = "day" | "week" | "month";
const CADENCE_OPTIONS: {
  key: string;
  label: string;
  unit: CadenceUnit;
  count: number;
  suffix: string;
  noun: string;
}[] = [
  {
    key: "week:1",
    label: "Weekly",
    unit: "week",
    count: 1,
    suffix: "/wk",
    noun: "week",
  },
  {
    key: "week:2",
    label: "Every 2 weeks",
    unit: "week",
    count: 2,
    suffix: "/2wk",
    noun: "2 weeks",
  },
  {
    key: "month:1",
    label: "Monthly",
    unit: "month",
    count: 1,
    suffix: "/mo",
    noun: "month",
  },
  {
    key: "day:1",
    label: "Daily (testing)",
    unit: "day",
    count: 1,
    suffix: "/day",
    noun: "day",
  },
];
const cadenceByKey = (key: string) =>
  CADENCE_OPTIONS.find((o) => o.key === key) ?? CADENCE_OPTIONS[2];

export interface MemberInfo {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  displayImg: string | null;
  username?: string;
}

const descriptionPlaceholder =
  "Describe your circle… (e.g. Friends and family saving together — everyone gets a turn)";
const today = dayjs();

function CreateGroup() {
  const theme = useTheme();
  const user = useSelector((state: RootState) => state.auth.user);
  if (!user) {
    return (
      <Typography variant="body1" color="error">
        You must be logged in to create a group.
      </Typography>
    );
  }
  const { data } = useGetStatusQuery();
  const PARTICIPATABLE_STATUSES = [
    "active",
    "complete",
    "pending",
    "verifying",
    "verification_failed",
  ];
  const canParticipate = PARTICIPATABLE_STATUSES.includes(
    data?.userStatus?.toLowerCase() ?? "",
  );
  const { data: profile } = useGetProfileQuery();
  const userCurrency = (COUNTRY_CURRENCY[profile?.country] || "CAD") as
    "USD" | "CAD";

  const [loading, setLoading] = useState(false);

  // Update currency when profile loads (defaultValues are captured before profile is available)
  useEffect(() => {
    if (profile?.country) {
      setValue("currency", userCurrency);
    }
  }, [profile?.country, userCurrency]);

  const [group] = useGroupMutation();
  const navigate = useNavigate();

  const [, setSoloEntry] = useState("");
  const [open, setOpen] = useState(false);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    setError,
    formState: { errors },
  } = useForm<CreateGroupData>({
    resolver: zodResolver(createGroupSchema),
    mode: "onBlur",
    defaultValues: {
      groupTitle: "",
      groupDescription: "",
      monthlyContribution: "100",
      dueDate: today.toDate(),
      intervalUnit: "month",
      intervalCount: 1,
      minMembers: 2,
      maxMembers: 10,
      isPrivate: false,
      members: [],
      currency: userCurrency,
    },
  });

  console.log(errors);

  const { fields: members, append } = useFieldArray({
    control,
    name: "members",
  });

  const checkUserValid = async (newUser: any) => {
    try {
      append(newUser);
      setSoloEntry("");
    } catch (error) {
      setError("members", {
        type: "manual",
        message: "User not found",
      });
      console.error("Error adding user:", error);
    }
  };

  const handleDelete = (id: string) => {
    const existingMembers = members.filter(
      (members: MemberInfo) => members.id !== id,
    );
    setValue("members", existingMembers);
  };

  const onSubmit = async (data: CreateGroupData) => {
    setLoading(true);

    const circle = {
      ...data,
    };

    try {
      const res = await group({ circle }).unwrap();
      navigate(`/circles/${res.id}`);
    } catch (e) {
      console.log({ e });
    } finally {
      setLoading(false);
    }
  };

  const cadence = cadenceByKey(
    `${watch("intervalUnit")}:${watch("intervalCount")}`,
  );
  const monthlyAmt = Number(watch("monthlyContribution")) || 0;
  const minPool = Math.max(monthlyAmt * (watch("minMembers") - 1), 0);
  const maxPool = Math.max(monthlyAmt * (watch("maxMembers") - 1), 0);

  const sectionCard = {
    borderRadius: 4,
    bgcolor: "background.paper",
    boxShadow: "0 1px 3px rgba(16,24,64,0.06), 0 4px 16px rgba(16,24,64,0.08)",
    overflow: "hidden",
    mb: 3,
  };

  const fieldLabel = (text: string, tooltip?: string) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.75 }}>
      <Typography
        sx={{
          fontSize: "0.72rem",
          fontWeight: 700,
          color: "text.secondary",
          textTransform: "uppercase",
          letterSpacing: "0.09em",
        }}
      >
        {text}
      </Typography>
      {tooltip && (
        <Tooltip title={tooltip} placement="right">
          <InfoOutlinedIcon
            sx={{ fontSize: "0.85rem", color: "text.disabled", cursor: "help" }}
          />
        </Tooltip>
      )}
    </Box>
  );

  const sectionHeader = (icon: React.ReactNode, title: string) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </Box>
      <Typography
        sx={{ fontWeight: 700, fontSize: "0.95rem", color: "text.primary" }}
      >
        {title}
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        maxWidth: 1100,
        mx: "auto",
        px: { xs: 2, sm: 4 },
        py: { xs: 2, sm: 4 },
      }}
    >
      {/* Page header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            fontSize: { xs: "1.5rem", sm: "1.85rem" },
            fontWeight: 800,
            color: "text.primary",
            letterSpacing: "-0.02em",
            lineHeight: 1.2,
          }}
        >
          Create a Circle
        </Typography>
        <Typography
          sx={{ fontSize: "0.95rem", color: "text.secondary", mt: 0.75 }}
        >
          Set up your lending circle and invite members to join.
        </Typography>
      </Box>

      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 380px" },
          gap: 3,
          alignItems: "start",
        }}
      >
        {/* ── Left column: Info + Settings ── */}
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {/* ── Section 1: Circle Info ── */}
          <Box sx={sectionCard}>
            <Box sx={{ height: 4, bgcolor: "primary.main" }} />
            <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
              {sectionHeader(
                <GroupIcon sx={{ fontSize: "1rem", color: "primary.main" }} />,
                "Circle Information",
              )}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                <Box>
                  {fieldLabel("Circle Name")}
                  <Controller
                    name="groupTitle"
                    control={control}
                    render={({ field }: { field: any }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="e.g. Dixon Friends Circle"
                        error={!!errors.groupTitle}
                        helperText={errors.groupTitle?.message}
                        inputProps={{
                          style: { fontSize: "1rem", fontWeight: 600 },
                        }}
                      />
                    )}
                  />
                </Box>
                <Box>
                  {fieldLabel(
                    "Description",
                    "Optional — give members context about the circle's purpose",
                  )}
                  <Controller
                    name="groupDescription"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        multiline
                        minRows={2}
                        placeholder={descriptionPlaceholder}
                        error={!!errors.groupDescription}
                        helperText={errors.groupDescription?.message}
                      />
                    )}
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          {/* ── Section 2: Circle Settings ── */}
          <Box sx={sectionCard}>
            <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
              {sectionHeader(
                <TuneIcon sx={{ fontSize: "1rem", color: "primary.main" }} />,
                "Circle Settings",
              )}

              {/* Payout frequency */}
              <Box sx={{ mb: 2.5 }}>
                {fieldLabel(
                  "Payout Frequency",
                  "How often the pot is paid out and rotates to the next member. Monthly is standard.",
                )}
                <Controller
                  name="intervalUnit"
                  control={control}
                  render={() => (
                    <TextField
                      select
                      fullWidth
                      value={cadence.key}
                      onChange={(e) => {
                        const opt = cadenceByKey(e.target.value);
                        setValue("intervalUnit", opt.unit);
                        setValue("intervalCount", opt.count);
                      }}
                    >
                      {CADENCE_OPTIONS.map((opt) => (
                        <MenuItem key={opt.key} value={opt.key}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Box>

              {/* Contribution amount + Start date */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2.5,
                  mb: 2.5,
                }}
              >
                <Box>
                  {fieldLabel(`Contribution per ${cadence.noun}`)}
                  <Controller
                    name="monthlyContribution"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        type="number"
                        error={!!errors.monthlyContribution}
                        helperText={errors.monthlyContribution?.message}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              {cadence.suffix}
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Box>
                <Box>
                  {fieldLabel("First Payout Date")}
                  <Controller
                    name="dueDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        disablePast
                        onChange={(date) =>
                          setValue("dueDate", dayjs(date).toDate())
                        }
                        value={dayjs(watch("dueDate"))}
                        sx={{ width: "100%" }}
                      />
                    )}
                  />
                </Box>
              </Box>

              {/* Currency — auto-detected, shown as a tinted info row */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 2,
                  py: 1.25,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                  mb: 2.5,
                }}
              >
                <InfoOutlinedIcon
                  sx={{ fontSize: "0.9rem", color: "primary.main" }}
                />
                <Typography
                  sx={{ fontSize: "0.8rem", color: "text.secondary" }}
                >
                  Currency is auto-detected from your profile:{" "}
                  <Typography
                    component="span"
                    sx={{
                      fontWeight: 700,
                      color: "text.primary",
                      fontSize: "0.8rem",
                    }}
                  >
                    {userCurrency}
                  </Typography>
                </Typography>
              </Box>

              {/* Min / Max participants */}
              <Box sx={{ mb: 2 }}>
                {fieldLabel(
                  "Participant Limits",
                  "Drag the ends to set how many people the circle needs to start and the most it will accept.",
                )}
                <Box sx={{ px: 1.5, pt: 1 }}>
                  <Slider
                    value={[watch("minMembers"), watch("maxMembers")]}
                    onChange={(_, val) => {
                      const [min, max] = val as number[];
                      setValue("minMembers", min, { shouldValidate: true });
                      setValue("maxMembers", max, { shouldValidate: true });
                    }}
                    min={MEMBER_RANGE.min}
                    max={MEMBER_RANGE.max}
                    step={1}
                    marks
                    disableSwap
                    valueLabelDisplay="auto"
                    getAriaLabel={(index) =>
                      index === 0 ? "Minimum members" : "Maximum members"
                    }
                  />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mt: 0.25,
                    }}
                  >
                    <Typography
                      sx={{ fontSize: "0.8rem", color: "text.secondary" }}
                    >
                      Min{" "}
                      <strong style={{ color: theme.palette.text.primary }}>
                        {watch("minMembers")}
                      </strong>
                    </Typography>
                    <Typography
                      sx={{ fontSize: "0.8rem", color: "text.secondary" }}
                    >
                      Max{" "}
                      <strong style={{ color: theme.palette.text.primary }}>
                        {watch("maxMembers")}
                      </strong>
                    </Typography>
                  </Box>
                  {(errors.minMembers || errors.maxMembers) && (
                    <Typography
                      sx={{ fontSize: "0.75rem", color: "error.main", mt: 0.5 }}
                    >
                      {errors.minMembers?.message ?? errors.maxMembers?.message}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Live payout range preview */}
              {monthlyAmt > 0 && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 1.5,
                    p: 2,
                    borderRadius: 2.5,
                    bgcolor: alpha(theme.palette.success.main, 0.04),
                    border: `1px solid ${alpha(theme.palette.success.main, 0.15)}`,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "0.78rem",
                        fontWeight: 600,
                        color: "text.secondary",
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        mb: 0.4,
                      }}
                    >
                      Min Payout
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "1.1rem",
                        fontWeight: 700,
                        color: "success.dark",
                      }}
                    >
                      ${minPool.toLocaleString()}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "0.78rem",
                        fontWeight: 600,
                        color: "text.secondary",
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        mb: 0.4,
                      }}
                    >
                      Max Payout
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "1.1rem",
                        fontWeight: 700,
                        color: "success.dark",
                      }}
                    >
                      ${maxPool.toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        {/* end left column */}

        {/* ── Right column: Participants + Submit ── */}
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {/* ── Section 3: Participants ── */}
          <Box sx={{ ...sectionCard, overflow: "visible" }}>
            <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
              {sectionHeader(
                <PeopleAltIcon
                  sx={{ fontSize: "1rem", color: "primary.main" }}
                />,
                "Participants",
              )}

              {/* Privacy toggle */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2,
                  py: 1.5,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  mb: 2.5,
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.88rem",
                      fontWeight: 600,
                      color: "text.primary",
                    }}
                  >
                    Private Circle
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: "text.secondary",
                      mt: 0.25,
                    }}
                  >
                    Only you can invite members
                  </Typography>
                </Box>
                <Controller
                  name="isPrivate"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      color="primary"
                    />
                  )}
                />
              </Box>

              <Stack spacing={2}>
                <AddNewUsers
                  potentialMembers={members}
                  handleUserSelect={checkUserValid}
                  errors={errors}
                />
                {members.length >= 1 && (
                  <Box
                    onClick={() => setOpen(true)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      px: 2,
                      py: 1.5,
                      borderRadius: 3,
                      cursor: "pointer",
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.primary.light, 0.1)} 100%)`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                      transition: "all 0.2s",
                      "&:hover": {
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
                        transform: "translateY(-1px)",
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.12)}`,
                      },
                    }}
                  >
                    <AvatarGroup
                      max={4}
                      sx={{
                        "& .MuiAvatar-root": {
                          width: 34,
                          height: 34,
                          fontSize: "0.8rem",
                          border: `2px solid ${theme.palette.background.paper}`,
                          bgcolor: theme.palette.primary.main,
                        },
                      }}
                    >
                      {members.map((member: MemberInfo) =>
                        member.displayImg === null ? (
                          <Avatar key={member.id}>
                            {member.firstName[0].toUpperCase()}
                            {member.lastName[0].toUpperCase()}
                          </Avatar>
                        ) : (
                          <ImageAvatar
                            key={member.id}
                            displayImg={member.displayImg}
                            firstName={member.firstName}
                          />
                        ),
                      )}
                    </AvatarGroup>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        sx={{
                          fontSize: "0.88rem",
                          fontWeight: 600,
                          color: "text.primary",
                        }}
                      >
                        {members.length} member{members.length > 1 ? "s" : ""}{" "}
                        added
                      </Typography>
                      <Typography
                        sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                      >
                        Click to view or remove
                      </Typography>
                    </Box>
                    <Chip
                      icon={<GroupIcon sx={{ fontSize: 14 }} />}
                      label="Manage"
                      size="small"
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        color: "primary.main",
                        fontWeight: 600,
                        fontSize: "0.72rem",
                        "& .MuiChip-icon": { color: "primary.main" },
                      }}
                    />
                    <ChevronRightIcon
                      sx={{ color: "primary.main", fontSize: 18 }}
                    />
                  </Box>
                )}
              </Stack>

              <UserListModalContent
                open={open}
                onClose={() => setOpen(false)}
                members={members}
                onDelete={(memberIdToRemove) => handleDelete(memberIdToRemove)}
              />
            </Box>
          </Box>

          {/* ── Submit ── */}
          <Box
            sx={{
              ...sectionCard,
              p: { xs: 2.5, sm: 3 },
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={!canParticipate || loading}
              sx={{
                py: 1.5,
                fontSize: "1rem",
                fontWeight: 700,
                borderRadius: "12px",
              }}
            >
              {loading ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                "Create Circle"
              )}
            </Button>
            {!canParticipate && (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                You must complete your contact information before you can create
                a circle.{" "}
                <a href="/onboarding?step=1" style={{ fontWeight: 600 }}>
                  Complete setup →
                </a>
              </Alert>
            )}
          </Box>
        </Box>
        {/* end right column */}
      </Box>
    </Box>
  );
}

export default CreateGroup;
